import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Shirt, RotateCcw, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import api from '../utils/api';

// SVG Avatar Component
const AvatarSVG = ({ bodyType, rotation, selectedProduct }) => {
  // Body dimensions based on type
  const bodyDimensions = {
    slim: { torsoWidth: 60, torsoHeight: 80, shoulderWidth: 70, hipWidth: 55, armWidth: 12 },
    normal: { torsoWidth: 70, torsoHeight: 80, shoulderWidth: 85, hipWidth: 65, armWidth: 14 },
    athletic: { torsoWidth: 80, torsoHeight: 85, shoulderWidth: 100, hipWidth: 70, armWidth: 18 },
    plus: { torsoWidth: 95, torsoHeight: 85, shoulderWidth: 110, hipWidth: 90, armWidth: 20 }
  };

  const dims = bodyDimensions[bodyType] || bodyDimensions.normal;
  const centerX = 200;
  const skinColor = '#d4a574';
  const pantsColor = '#1a1a1a';
  
  // Calculate perspective based on rotation
  const rotationDeg = (rotation * 180 / Math.PI) % 360;
  const perspectiveScale = Math.cos(rotation) * 0.3 + 0.7; // 0.4 to 1
  const isFront = Math.cos(rotation) >= 0;
  const sideOffset = Math.sin(rotation) * 30;

  return (
    <svg viewBox="0 0 400 500" className="w-full h-full max-h-[60vh]">
      <defs>
        {/* Gradient for 3D effect */}
        <linearGradient id="skinGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#c49a6c" />
          <stop offset="50%" stopColor={skinColor} />
          <stop offset="100%" stopColor="#b8956a" />
        </linearGradient>
        <linearGradient id="shirtGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e0e0e0" />
        </linearGradient>
        <linearGradient id="pantsGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0a0a0a" />
          <stop offset="50%" stopColor={pantsColor} />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
        {/* Shadow filter */}
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3"/>
        </filter>
        {/* Product texture */}
        {selectedProduct?.images?.[0] && (
          <pattern id="productTexture" patternUnits="objectBoundingBox" width="1" height="1">
            <image 
              href={selectedProduct.images[0]} 
              x="0" y="0" 
              width="100%" height="100%" 
              preserveAspectRatio="xMidYMid slice"
            />
          </pattern>
        )}
      </defs>
      
      {/* Background circle */}
      <circle cx={centerX} cy="250" r="180" fill="url(#radialBg)" opacity="0.1" />
      
      {/* Platform/Shadow */}
      <ellipse cx={centerX + sideOffset * 0.5} cy="480" rx={60 * perspectiveScale} ry="15" fill="#000" opacity="0.3" />
      
      {/* Group with perspective transform */}
      <g transform={`translate(${sideOffset}, 0)`} filter="url(#shadow)">
        
        {/* Legs */}
        <g>
          {/* Left leg */}
          <path 
            d={`M ${centerX - dims.hipWidth/3} 320 
                Q ${centerX - dims.hipWidth/3 - 5} 380 ${centerX - dims.hipWidth/3 - 8} 450
                L ${centerX - dims.hipWidth/3 + 15} 450
                Q ${centerX - dims.hipWidth/3 + 10} 380 ${centerX - dims.hipWidth/3 + 20} 320`}
            fill="url(#pantsGradient)"
          />
          {/* Right leg */}
          <path 
            d={`M ${centerX + dims.hipWidth/3} 320 
                Q ${centerX + dims.hipWidth/3 + 5} 380 ${centerX + dims.hipWidth/3 + 8} 450
                L ${centerX + dims.hipWidth/3 - 15} 450
                Q ${centerX + dims.hipWidth/3 - 10} 380 ${centerX + dims.hipWidth/3 - 20} 320`}
            fill="url(#pantsGradient)"
          />
          {/* Shoes */}
          <ellipse cx={centerX - dims.hipWidth/3} cy="460" rx="18" ry="12" fill="#111" />
          <ellipse cx={centerX + dims.hipWidth/3} cy="460" rx="18" ry="12" fill="#111" />
        </g>
        
        {/* Hips/Lower body */}
        <ellipse 
          cx={centerX} 
          cy="320" 
          rx={dims.hipWidth/2 * perspectiveScale} 
          ry="25" 
          fill="url(#pantsGradient)" 
        />
        
        {/* Torso - with product or skin */}
        <path 
          d={`M ${centerX - dims.shoulderWidth/2 * perspectiveScale} 170
              Q ${centerX - dims.torsoWidth/2 * perspectiveScale} 250 ${centerX - dims.hipWidth/2 * perspectiveScale} 310
              L ${centerX + dims.hipWidth/2 * perspectiveScale} 310
              Q ${centerX + dims.torsoWidth/2 * perspectiveScale} 250 ${centerX + dims.shoulderWidth/2 * perspectiveScale} 170
              Z`}
          fill={selectedProduct ? "url(#productTexture)" : "url(#shirtGradient)"}
          stroke={selectedProduct ? "none" : "#ccc"}
          strokeWidth="1"
        />
        
        {/* Arms */}
        <g>
          {/* Left arm */}
          <path 
            d={`M ${centerX - dims.shoulderWidth/2 * perspectiveScale} 170
                Q ${centerX - dims.shoulderWidth/2 * perspectiveScale - 25} 230 
                  ${centerX - dims.shoulderWidth/2 * perspectiveScale - 30} 300
                L ${centerX - dims.shoulderWidth/2 * perspectiveScale - 30 + dims.armWidth} 300
                Q ${centerX - dims.shoulderWidth/2 * perspectiveScale - 15} 230 
                  ${centerX - dims.shoulderWidth/2 * perspectiveScale + 10} 175`}
            fill="url(#skinGradient)"
          />
          {/* Right arm */}
          <path 
            d={`M ${centerX + dims.shoulderWidth/2 * perspectiveScale} 170
                Q ${centerX + dims.shoulderWidth/2 * perspectiveScale + 25} 230 
                  ${centerX + dims.shoulderWidth/2 * perspectiveScale + 30} 300
                L ${centerX + dims.shoulderWidth/2 * perspectiveScale + 30 - dims.armWidth} 300
                Q ${centerX + dims.shoulderWidth/2 * perspectiveScale + 15} 230 
                  ${centerX + dims.shoulderWidth/2 * perspectiveScale - 10} 175`}
            fill="url(#skinGradient)"
          />
          {/* Hands */}
          <ellipse cx={centerX - dims.shoulderWidth/2 * perspectiveScale - 25} cy="310" rx="12" ry="15" fill={skinColor} />
          <ellipse cx={centerX + dims.shoulderWidth/2 * perspectiveScale + 25} cy="310" rx="12" ry="15" fill={skinColor} />
        </g>
        
        {/* Neck */}
        <rect 
          x={centerX - 15} 
          y="135" 
          width="30" 
          height="40" 
          rx="10"
          fill="url(#skinGradient)" 
        />
        
        {/* Head */}
        <ellipse 
          cx={centerX} 
          cy="100" 
          rx={35 * perspectiveScale} 
          ry="45" 
          fill="url(#skinGradient)" 
        />
        
        {/* Face features (front only) */}
        {isFront && (
          <g opacity={Math.abs(Math.cos(rotation))}>
            {/* Eyes */}
            <ellipse cx={centerX - 12} cy="95" rx="5" ry="3" fill="#333" />
            <ellipse cx={centerX + 12} cy="95" rx="5" ry="3" fill="#333" />
            {/* Nose */}
            <path d={`M ${centerX} 100 Q ${centerX + 3} 110 ${centerX} 115`} fill="none" stroke="#c49a6c" strokeWidth="2" />
            {/* Mouth */}
            <path d={`M ${centerX - 10} 125 Q ${centerX} 130 ${centerX + 10} 125`} fill="none" stroke="#b08070" strokeWidth="2" />
          </g>
        )}
        
        {/* Hair */}
        <ellipse 
          cx={centerX} 
          cy="70" 
          rx={38 * perspectiveScale} 
          ry="30" 
          fill="#2a2a2a" 
        />
      </g>
      
      {/* Rotation indicator */}
      <g transform={`translate(${centerX}, 490)`}>
        <circle r="20" fill="none" stroke="white" strokeWidth="1" opacity="0.3" />
        <line 
          x1="0" y1="0" 
          x2={Math.sin(rotation) * 15} 
          y2={-Math.cos(rotation) * 15} 
          stroke="white" 
          strokeWidth="2"
          opacity="0.5"
        />
      </g>
    </svg>
  );
};

// Main Component
const VirtualTryOn = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [bodyType, setBodyType] = useState('normal');
  const [rotation, setRotation] = useState(0);
  const [autoRotate, setAutoRotate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [productPage, setProductPage] = useState(0);
  const productsPerPage = 8;
  const rotationRef = useRef(null);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/products');
        // Filter products that are clothing
        const clothingProducts = response.data.filter(p => 
          p.stock > 0 && 
          p.images?.length > 0
        );
        setProducts(clothingProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Auto rotation
  useEffect(() => {
    if (autoRotate) {
      rotationRef.current = setInterval(() => {
        setRotation(prev => prev + 0.03);
      }, 30);
    }
    return () => {
      if (rotationRef.current) clearInterval(rotationRef.current);
    };
  }, [autoRotate]);

  const bodyTypes = [
    { id: 'slim', name: 'Delgado', desc: 'Complexión delgada' },
    { id: 'normal', name: 'Normal', desc: 'Complexión promedio' },
    { id: 'athletic', name: 'Atlético', desc: 'Complexión musculosa' },
    { id: 'plus', name: 'Robusto', desc: 'Complexión robusta' }
  ];

  const paginatedProducts = products.slice(
    productPage * productsPerPage,
    (productPage + 1) * productsPerPage
  );

  const totalPages = Math.ceil(products.length / productsPerPage);

  const handleRotate = (direction) => {
    setRotation(prev => prev + (direction * 0.5));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0a] to-[#111] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">Volver al catálogo</span>
            </Link>
            <h1 className="text-lg sm:text-xl font-bold tracking-[0.15em] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              PROBADOR VIRTUAL
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      <div className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Left Panel - Body Type & Controls */}
            <div className="lg:col-span-3 space-y-4">
              {/* Body Type Selection */}
              <div className="bg-[#111] border border-white/10 p-4">
                <h3 className="text-xs uppercase tracking-wider text-white/60 mb-4 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Tipo de Cuerpo
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {bodyTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setBodyType(type.id)}
                      className={`p-3 border text-center transition-all ${
                        bodyType === type.id
                          ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <span className="text-sm font-medium block">{type.name}</span>
                      <span className="text-[10px] text-white/40">{type.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Rotation Controls */}
              <div className="bg-[#111] border border-white/10 p-4">
                <h3 className="text-xs uppercase tracking-wider text-white/60 mb-4">Rotación</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRotate(-1)}
                    className="flex-1 p-3 border border-white/10 hover:border-white/30 transition-colors flex items-center justify-center gap-1"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span className="text-xs">Izq</span>
                  </button>
                  <button
                    onClick={() => setAutoRotate(!autoRotate)}
                    className={`flex-1 p-3 border transition-colors ${
                      autoRotate ? 'border-purple-500 bg-purple-500/20 text-purple-300' : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <span className="text-xs">{autoRotate ? '⏸ Parar' : '▶ Auto'}</span>
                  </button>
                  <button
                    onClick={() => handleRotate(1)}
                    className="flex-1 p-3 border border-white/10 hover:border-white/30 transition-colors flex items-center justify-center gap-1"
                  >
                    <span className="text-xs">Der</span>
                    <RotateCcw className="w-4 h-4 scale-x-[-1]" />
                  </button>
                </div>
                <button
                  onClick={() => setRotation(0)}
                  className="w-full mt-2 p-2 border border-white/10 text-xs text-white/50 hover:text-white hover:border-white/30 transition-colors"
                >
                  Resetear vista frontal
                </button>
              </div>

              {/* Selected Product Info */}
              {selectedProduct && (
                <div className="bg-[#111] border border-purple-500/30 p-4">
                  <h3 className="text-xs uppercase tracking-wider text-purple-400 mb-3">Prenda Seleccionada</h3>
                  <div className="flex gap-3">
                    <img 
                      src={selectedProduct.images[0]} 
                      alt={selectedProduct.name}
                      className="w-20 h-20 object-cover border border-white/10"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{selectedProduct.name}</p>
                      <p className="text-xs text-white/50">{selectedProduct.brand}</p>
                      <p className="text-base font-bold text-green-400 mt-2">${selectedProduct.price?.toLocaleString()}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedProduct(null)}
                    className="w-full mt-3 p-2 border border-red-500/30 text-red-400 text-xs hover:bg-red-500/10 transition-colors"
                  >
                    Quitar prenda
                  </button>
                </div>
              )}
            </div>

            {/* Center - Avatar Display */}
            <div className="lg:col-span-5 flex flex-col items-center justify-center min-h-[50vh] lg:min-h-[70vh] bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border border-white/10 relative">
              <AvatarSVG 
                bodyType={bodyType}
                rotation={rotation}
                selectedProduct={selectedProduct}
              />
              
              {/* Instructions */}
              <div className="absolute bottom-4 left-4 right-4 text-center">
                <p className="text-xs text-white/30 bg-black/50 px-4 py-2 inline-block rounded-full">
                  Usa los controles de rotación • Selecciona una prenda a la derecha
                </p>
              </div>
              
              {!selectedProduct && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-lg">
                    <Shirt className="w-12 h-12 mx-auto mb-3 text-white/30" />
                    <p className="text-sm text-white/50">Selecciona una prenda</p>
                    <p className="text-xs text-white/30">para ver cómo te quedaría</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Product Selection */}
            <div className="lg:col-span-4 bg-[#111] border border-white/10 p-4">
              <h3 className="text-xs uppercase tracking-wider text-white/60 mb-4 flex items-center gap-2">
                <Shirt className="w-4 h-4" />
                Seleccionar Prenda ({products.length})
              </h3>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-white/20 border-t-purple-500 rounded-full animate-spin"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-4 lg:grid-cols-2 gap-2 mb-4">
                    {paginatedProducts.map(product => (
                      <button
                        key={product.id}
                        onClick={() => setSelectedProduct(product)}
                        className={`relative group overflow-hidden border transition-all ${
                          selectedProduct?.id === product.id
                            ? 'border-purple-500 ring-2 ring-purple-500/50'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="aspect-square">
                          <img 
                            src={product.images[0]} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-2">
                            <p className="text-[10px] text-white truncate">{product.name}</p>
                            <p className="text-xs font-bold text-green-400">${product.price?.toLocaleString()}</p>
                          </div>
                        </div>
                        {selectedProduct?.id === product.id && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-white/10 pt-4">
                      <button
                        onClick={() => setProductPage(prev => Math.max(0, prev - 1))}
                        disabled={productPage === 0}
                        className="p-2 border border-white/10 disabled:opacity-30 hover:border-white/30 transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-xs text-white/50">
                        Página {productPage + 1} de {totalPages}
                      </span>
                      <button
                        onClick={() => setProductPage(prev => Math.min(totalPages - 1, prev + 1))}
                        disabled={productPage >= totalPages - 1}
                        className="p-2 border border-white/10 disabled:opacity-30 hover:border-white/30 transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Tips */}
              <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20">
                <p className="text-xs text-purple-300 font-medium mb-1">✨ Tip</p>
                <p className="text-xs text-white/50">
                  Prueba diferentes tipos de cuerpo para ver cómo te quedaría la prenda según tu complexión.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualTryOn;
